<template>
  <SidebarNavigation :uri="uri">
    <div class="w-full mx-auto">
      <h2 class="flex justify-center font-bold text-xl sm:text-2xl lg:text-3xl mb-4 sm:mb-6 border-b-2 border-gray-200 pb-3 sm:pb-4 px-4 sm:px-0 text-gray-800">{{ __('Link-in-Bio Page Generator') }}</h2>
            <div class="md:mt-10 sm:mt-3 justify-center flex flex-col lg:flex-row w-full mx-auto card-properties gap-4 lg:gap-6 px-4 sm:px-6 lg:px-0">
                <!-- Form Section -->
                <div class="card-form w-full lg:w-1/2 mx-auto">
                    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                        <!-- Profile Section -->
                        <div class="w-full mb-6 pb-6 border-b border-gray-200">
                            <h3 class="text-xl font-semibold mb-5 text-gray-800 flex items-center gap-2">
                                <icon name="card_user" class="w-5 h-5 text-green-600" />
                                Profile Information
                            </h3>

                            <div class="flex w-full mb-6">
                                <div class="w-full flex flex-col items-center">
                                    <div class="relative mb-4 group">
                                        <img
                                            v-if="form.profile_picture"
                                            :src="form.profile_picture"
                                            alt="Profile"
                                            class="w-36 h-36 rounded-full object-cover border-4 border-gray-200 shadow-lg transition-transform group-hover:scale-105"
                                        />
                                        <div v-else class="w-36 h-36 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center border-4 border-gray-300 shadow-lg">
                                            <icon name="user" class="w-20 h-20 text-gray-400" />
                                        </div>
                                        <label for="profilePicture" class="absolute bottom-2 right-2 bg-green-600 text-white rounded-full p-3 cursor-pointer hover:bg-green-700 transition-all shadow-lg hover:shadow-xl hover:scale-110">
                                            <icon name="camera" class="w-5 h-5" />
                                        </label>
                                        <input id="profilePicture" @change="profilePictureChange($event)" class="hidden" type="file" accept="image/*">
                                    </div>
                                    <p class="text-xs text-gray-500 mt-2">Click the camera icon to upload</p>
                                </div>
                            </div>

                            <div class="flex w-full mb-4">
                                <text-input v-model="form.name" :error="form.errors.name" class="w-full pr-6 pb-4" :label="__('Name')" type="text" placeholder="Your Name" />
                            </div>
                            <div class="flex w-full mb-4">
                                <text-input v-model="form.title" :error="form.errors.title" class="w-full pr-6 pb-4" :label="__('Title / Role')" type="text" placeholder="e.g., Digital Creator, Entrepreneur" />
                            </div>
                            <div class="flex w-full">
                                <textarea-input
                                    v-model="form.bio"
                                    :error="form.errors.bio"
                                    class="w-full pr-6 pb-4"
                                    :rows="3"
                                    label="Bio / Description"
                                    placeholder="Tell people about yourself..."
                                />
                            </div>
                        </div>

                        <!-- Links Section -->
                        <div class="w-full mb-6">
                            <div class="flex justify-between items-center mb-5">
                                <h3 class="text-xl font-semibold text-gray-800 flex items-center gap-2">
                                    <icon name="website" class="w-5 h-5 text-green-600" />
                                    Links
                                </h3>
                                <button
                                    @click="addLink()"
                                    class="text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 active:from-green-800 active:to-green-900 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-semibold flex items-center gap-2 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5 active:translate-y-0"
                                >
                                    <icon name="plus" class="w-5 h-5 text-white fill-white" /> Add Link
                                </button>
                            </div>

                            <div v-if="form.links.length === 0" class="text-center py-12 text-gray-400 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:border-green-400 hover:bg-green-50 transition-colors">
                                <icon name="website" class="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                <p class="text-sm font-medium mb-1">No links added yet</p>
                                <p class="text-xs">Click "Add Link" to get started!</p>
                            </div>

                            <div v-for="(link, index) in form.links" :key="link.id" class="mb-4 p-4 border-2 border-gray-200 rounded-lg bg-gray-50 hover:border-green-400 hover:bg-green-50 transition-all">
                                <div class="flex justify-between items-center mb-3">
                                    <span class="text-xs font-semibold text-gray-700 bg-white px-3 py-1.5 rounded-full border border-gray-300">Link {{ index + 1 }}</span>
                                    <div class="flex gap-1">
                                        <button
                                            v-if="index > 0"
                                            @click="moveLinkUp(index)"
                                            class="p-2 text-gray-500 hover:text-gray-700 hover:bg-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                                            title="Move up"
                                        >
                                            <icon name="up" class="w-4 h-4" />
                                        </button>
                                        <button
                                            v-if="index < form.links.length - 1"
                                            @click="moveLinkDown(index)"
                                            class="p-2 text-gray-500 hover:text-gray-700 hover:bg-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                                            title="Move down"
                                        >
                                            <icon name="down" class="w-4 h-4" />
                                        </button>
                                        <button
                                            @click="removeLink(index)"
                                            class="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-lg transition-colors shadow-sm hover:shadow-md"
                                            title="Remove link"
                                        >
                                            <icon name="trash" class="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                <div class="flex w-full mb-3">
                                    <text-input
                                        v-model="link.title"
                                        :error="form.errors[`links.${index}.title`]"
                                        class="w-full pr-6 pb-4"
                                        label="Link Title"
                                        placeholder="e.g., My Website, Instagram"
                                    />
                                </div>
                                <div class="flex w-full">
                                    <text-input
                                        v-model="link.url"
                                        :error="form.errors[`links.${index}.url`] || validationErrors[`links.${index}.url`]"
                                        @blur="validateLinkUrl(link, index)"
                                        class="w-full pr-6 pb-4"
                                        label="URL"
                                        type="url"
                                        placeholder="https://example.com"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Lead Capture Section -->
                        <div class="w-full mb-6 pb-6 border-b border-gray-200">
                            <h3 class="text-xl font-semibold mb-5 text-gray-800 flex items-center gap-2">
                                <icon name="contact" class="w-5 h-5 text-green-600" />
                                Lead Capture
                            </h3>

                            <div class="flex items-center gap-3 mb-4">
                                <label class="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        v-model="form.enable_lead_capture"
                                        class="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span class="text-sm font-medium text-gray-700">Enable Lead Capture Form</span>
                                </label>
                            </div>

                            <div v-if="form.enable_lead_capture" class="mt-4">
                                <label class="block mb-2 text-sm font-semibold text-gray-700">Form Type</label>
                                <select v-model="form.lead_form_type" class="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white transition-all">
                                    <option value="email_only">Email Only</option>
                                    <option value="name_email">Name + Email</option>
                                </select>
                                <p class="text-xs text-gray-500 mt-2">Choose which fields to collect from visitors</p>
                            </div>
                        </div>

                        <!-- Customization Section -->
                        <div class="w-full mt-4 pt-6 border-t border-gray-200">
                            <h3 class="text-xl font-semibold mb-5 text-gray-800 flex items-center gap-2">
                                <icon name="palette" class="w-5 h-5 text-green-600" />
                                Customization
                            </h3>

                            <div class="flex w-full mb-5 gap-4">
                                <div class="w-1/2">
                                    <label class="block mb-2 text-sm font-semibold text-gray-700">Button Style</label>
                                    <select v-model="form.button_style" class="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white transition-all">
                                        <option value="rounded">Rounded</option>
                                        <option value="square">Square</option>
                                        <option value="pill">Pill</option>
                                    </select>
                                </div>
                                <div class="w-1/2">
                                    <label class="block mb-2 text-sm font-semibold text-gray-700">Button Color</label>
                                    <div class="flex items-center gap-2">
                                        <input
                                            v-model="form.button_color"
                                            type="color"
                                            class="w-16 h-10 border-2 border-gray-300 rounded-lg cursor-pointer hover:border-green-500 transition-colors"
                                        />
                                        <input
                                            type="text"
                                            :value="form.button_color"
                                            @input="form.button_color = $event.target.value"
                                            class="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm font-mono"
                                            placeholder="#17803d"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div class="flex w-full mb-5 gap-4">
                                <div class="w-1/2">
                                    <label class="block mb-2 text-sm font-semibold text-gray-700">Background Color</label>
                                    <div class="flex items-center gap-2">
                                        <input
                                            v-model="form.background_color"
                                            type="color"
                                            class="w-16 h-10 border-2 border-gray-300 rounded-lg cursor-pointer hover:border-green-500 transition-colors"
                                        />
                                        <input
                                            type="text"
                                            :value="form.background_color"
                                            @input="form.background_color = $event.target.value"
                                            class="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm font-mono"
                                            placeholder="#FFFFFF"
                                        />
                                    </div>
                                </div>
                                <div class="w-1/2">
                                    <label class="block mb-2 text-sm font-semibold text-gray-700">Text Color</label>
                                    <div class="flex items-center gap-2">
                                        <input
                                            v-model="form.text_color"
                                            type="color"
                                            class="w-16 h-10 border-2 border-gray-300 rounded-lg cursor-pointer hover:border-green-500 transition-colors"
                                        />
                                        <input
                                            type="text"
                                            :value="form.text_color"
                                            @input="form.text_color = $event.target.value"
                                            class="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm font-mono"
                                            placeholder="#000000"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div class="flex w-full flex-col pr-6 mb-4">
                                <label class="block mb-2 text-sm font-semibold text-gray-700" for="backgroundImageLink">
                                    Background Image (Optional)
                                </label>
                                <label for="backgroundImageLink" class="block w-full px-4 py-3 text-sm text-gray-700 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 hover:border-green-400 transition-colors">
                                    <div class="flex items-center gap-2">
                                        <icon name="camera" class="w-5 h-5 text-gray-400" />
                                        <span class="text-gray-600">Click to upload background image</span>
                                    </div>
                                </label>
                                <input id="backgroundImageLink" @change="backgroundImageInputChange($event)" class="hidden" type="file" accept="image/*">
                                <p v-if="form.background_image" class="text-xs text-green-600 mt-2 flex items-center gap-1">
                                    <icon name="tick" class="w-4 h-4" />
                                    Background image uploaded
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Preview Section -->
                <div class="card-items flex w-full lg:w-1/2 mx-auto py-0 lg:sticky lg:top-6 lg:self-start">
                    <div class="flex flex-wrap flex-col items-center w-full">
                        <div class="w-full mb-4">
                            <h3 class="text-xl font-semibold text-gray-800 flex items-center gap-2 mb-2">
                                <icon name="aperture" class="w-5 h-5 text-green-600" />
                                Live Preview
                            </h3>
                            <p class="text-xs text-gray-500">See how your page will look</p>
                        </div>
                        <div
                            class="link-bio-preview w-full max-w-md mx-auto rounded-xl shadow-2xl overflow-hidden border-4 border-gray-100 overflow-y-auto max-h-[600px] lg:max-h-none"
                            :style="{
                                backgroundColor: form.background_color,
                                backgroundImage: form.background_image ? `url(${form.background_image})` : 'none',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                minHeight: '500px'
                            }"
                            ref="linkBioPreview"
                        >
                            <!-- Profile Section -->
                            <div class="p-8 text-center" :style="{ color: form.text_color }">
                                <div class="mb-5">
                                    <img
                                        v-if="form.profile_picture"
                                        :src="form.profile_picture"
                                        alt="Profile"
                                        class="w-28 h-28 rounded-full object-cover mx-auto border-4 border-white shadow-xl"
                                    />
                                    <div v-else class="w-28 h-28 rounded-full bg-white mx-auto border-4 border-white shadow-xl flex items-center justify-center">
                                        <icon name="user" class="w-16 h-16 text-gray-400" />
                                    </div>
                                </div>

                                <h1 v-if="form.name" class="text-3xl font-bold mb-2">{{ form.name }}</h1>
                                <p v-if="form.title" class="text-base opacity-90 mb-3 font-medium">{{ form.title }}</p>
                                <p v-if="form.bio" class="text-sm opacity-80 mb-8 leading-relaxed max-w-sm mx-auto">{{ form.bio }}</p>
                            </div>

                            <!-- Links Section -->
                            <div class="px-6 pb-8">
                                <div
                                    v-for="(link, index) in form.links"
                                    :key="link.id"
                                    class="mb-3"
                                >
                                    <a
                                        :href="link.url || '#'"
                                        target="_blank"
                                        class="block w-full py-4 px-6 text-center font-semibold transition-all hover:opacity-90 hover:shadow-xl hover:scale-[1.02] transform"
                                        :class="getButtonClass()"
                                        :style="{
                                            backgroundColor: form.button_color,
                                            color: '#FFFFFF'
                                        }"
                                    >
                                        {{ link.title || `Link ${index + 1}` }}
                                    </a>
                                </div>

                                <div v-if="form.links.length === 0" class="text-center py-12 text-gray-400">
                                    <icon name="website" class="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p class="text-sm">Add links to see them here</p>
                                </div>
                            </div>

                            <!-- Lead Capture Form Preview -->
                            <div v-if="form.enable_lead_capture" class="px-6 pb-8">
                                <ContactForm
                                    :profile-id="999"
                                    :form-type="form.lead_form_type"
                                    :button-color="form.button_color"
                                    :text-color="form.text_color"
                                    :border-color="form.text_color + '20'"
                                />
                            </div>
                        </div>

                        <!-- Action Buttons -->
                        <div class="flex flex-col justify-center gap-4 py-4 sm:py-6 w-full px-4 sm:px-0">
                            <button
                                class="text-white flex justify-center bg-gradient-to-r from-green-600 to-green-700 border-0 focus:outline-none rounded-lg px-4 sm:px-8 py-3 sm:py-3.5 gap-2 items-center font-semibold shadow-lg hover:shadow-xl hover:from-green-700 hover:to-green-800 active:from-green-800 active:to-green-900 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-sm sm:text-base"
                                :disabled="loading || form.processing"
                                @click="generateLinkInBioUrl()"
                            >
                                <icon v-if="!loading && !form.processing" name="send" class="w-5 h-5 text-white fill-white" />
                                <span v-if="loading || form.processing" class="animate-spin">⏳</span>
                                {{ (loading || form.processing) ? 'Generating...' : 'Generate Link' }}
                            </button>

                            <!-- Generated URL Display -->
                            <div v-if="generatedUrl" class="mt-4 p-5 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl shadow-md">
                                <div class="flex items-center gap-2 mb-3">
                                    <icon name="check" class="w-5 h-5 text-green-600" />
                                    <p class="text-sm font-semibold text-green-800">Your Link-in-Bio page is ready!</p>
                                </div>
                                <div class="flex items-center gap-2 mb-4">
                                    <input
                                        type="text"
                                        :value="generatedUrl"
                                        readonly
                                        class="flex-1 px-4 py-2.5 border-2 border-green-300 rounded-lg bg-white text-sm text-gray-700 font-mono focus:outline-none focus:ring-2 focus:ring-green-500"
                                        ref="urlInput"
                                    />
                                    <button
                                        @click="copyUrl()"
                                        :disabled="copied"
                                        :class="[
                                            'px-5 py-2.5 rounded-lg transition-all shadow-md hover:shadow-lg font-medium text-sm flex items-center gap-2',
                                            copied
                                                ? 'bg-green-600 text-white cursor-default'
                                                : 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800'
                                        ]"
                                    >
                                        <icon v-if="!copied" name="file" class="w-4 h-4" />
                                        <icon v-else name="tick" class="w-4 h-4" />
                                        {{ copied ? 'Copied!' : 'Copy' }}
                                    </button>
                                </div>
                                <div class="flex items-center gap-3 pt-3 border-t border-green-200">
                                    <a
                                        :href="generatedUrl"
                                        target="_blank"
                                        class="text-sm text-green-700 hover:text-green-800 font-medium flex items-center gap-2 transition-colors"
                                    >
                                        <icon name="arrow-right" class="w-4 h-4" />
                                        Open in new tab
                                    </a>
                                    <span class="text-gray-300">|</span>
                                    <button
                                        @click="downloadAsPNG()"
                                        class="text-sm text-green-700 hover:text-green-800 font-medium flex items-center gap-2 transition-colors"
                                    >
                                        <icon name="download" class="w-4 h-4" />
                                        Download as Image
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
    </div>
    <!-- Loading Process -->
    <div v-if="form.processing" class="processing-overlay">
      <div class="background" />
      <div class="loader">
        <svg width="200px" height="200px" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid" style="background: none;">
          <circle cx="75" cy="50" fill="#ffffff" r="6.39718">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.875s" />
          </circle>
          <circle cx="67.678" cy="67.678" fill="#ffffff" r="4.8">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.75s" />
          </circle>
          <circle cx="50" cy="75" fill="#ffffff" r="4.8">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.625s" />
          </circle>
          <circle cx="32.322" cy="67.678" fill="#ffffff" r="4.8">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.5s" />
          </circle>
          <circle cx="25" cy="50" fill="#ffffff" r="4.8">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.375s" />
          </circle>
          <circle cx="32.322" cy="32.322" fill="#ffffff" r="4.80282">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.25s" />
          </circle>
          <circle cx="50" cy="25" fill="#ffffff" r="6.40282">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.125s" />
          </circle>
          <circle cx="67.678" cy="32.322" fill="#ffffff" r="7.99718">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="0s" />
          </circle>
        </svg>
      </div>
    </div>
    <!-- Loading Process -->
  </SidebarNavigation>
</template>

<script>
import Layout from '@/Shared/ToolLayout'
import TextInput from '@/Shared/TextInput'
import Icon from '@/Shared/Icon'
import {toPng } from 'html-to-image';
import TextareaInput from '../../Shared/TextareaInput.vue'
import SidebarNavigation from '../../Shared/SidebarNavigation.vue'
import ContactForm from '@/Shared/ContactForm.vue'
import { validateURL, validateFileSize, validateFileType } from '../../utils/validation.js'

export default {
    components: {
        SidebarNavigation,
        TextareaInput,
        Icon,
        TextInput,
        ContactForm,
    },
    layout: Layout,
    props: {
        title: String,
        uri: {
            type: String,
            required: false
        },
    },
    remember: 'form',
    data() {
        return {
            loading: false,
            generatedUrl: null,
            validationErrors: {},
            form: this.$inertia.form({
                name: '',
                title: '',
                bio: '',
                profile_picture: null,
                links: [],
                button_style: 'rounded',
                button_color: '#17803d',
                background_color: '#FFFFFF',
                text_color: '#000000',
                background_image: null,
                enable_lead_capture: false,
                lead_form_type: 'email_only',
            }),
        }
    },
    created() {
        // Set default values
        this.setForm({
            name: 'Your Name',
            title: 'Digital Creator',
            bio: 'Welcome to my link-in-bio page! Click the links below to connect with me.',
            profile_picture: null,
            links: [
                { id: this.generateLinkId(), title: 'My Website', url: 'https://example.com' },
                { id: this.generateLinkId(), title: 'Instagram', url: 'https://instagram.com/yourhandle' },
            ],
            button_style: 'rounded',
            button_color: '#17803d',
            background_color: '#FFFFFF',
            text_color: '#000000',
            background_image: null,
        })
    },
    methods: {
        /**
         * Generate unique ID for links
         */
        generateLinkId() {
            return 'link_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
        },

        /**
         * Add a new link
         */
        addLink() {
            this.form.links.push({
                id: this.generateLinkId(),
                title: '',
                url: ''
            })
        },

        /**
         * Remove a link
         */
        removeLink(index) {
            this.form.links.splice(index, 1)
        },

        /**
         * Move link up
         */
        moveLinkUp(index) {
            if (index > 0) {
                const link = this.form.links.splice(index, 1)[0]
                this.form.links.splice(index - 1, 0, link)
            }
        },

        /**
         * Move link down
         */
        moveLinkDown(index) {
            if (index < this.form.links.length - 1) {
                const link = this.form.links.splice(index, 1)[0]
                this.form.links.splice(index + 1, 0, link)
            }
        },

        /**
         * Get button CSS class based on style
         */
        getButtonClass() {
            switch(this.form.button_style) {
                case 'square':
                    return 'rounded-none'
                case 'pill':
                    return 'rounded-full'
                case 'rounded':
                default:
                    return 'rounded-lg'
            }
        },

        /**
         * Handle profile picture upload
         */
        profilePictureChange(event) {
            if(event.target.files.length && event.target.files[0]){
                const file = event.target.files[0]
                const validation = this.validateFileUpload(file, 'image')
                if (!validation.valid) {
                    event.target.value = ''
                    return
                }

                const self = this
                const reader = new FileReader()
                reader.onload = function() {
                    self.form.profile_picture = reader.result
                    if (window.$toast) {
                        window.$toast.success('Profile picture uploaded successfully')
                    }
                }
                reader.onerror = function() {
                    if (window.$toast) {
                        window.$toast.error('Failed to read file. Please try again.')
                    }
                    event.target.value = ''
                }
                reader.onabort = function() {
                    if (window.$toast) {
                        window.$toast.warning('File reading was cancelled')
                    }
                }
                reader.readAsDataURL(file)
            }
        },

        /**
         * Handle background image upload
         */
        backgroundImageInputChange(event) {
            if(event.target.files.length && event.target.files[0]){
                const file = event.target.files[0]
                const validation = this.validateFileUpload(file, 'image')
                if (!validation.valid) {
                    event.target.value = ''
                    return
                }

                const self = this
                const reader = new FileReader()
                reader.onload = function() {
                    self.form.background_image = reader.result
                    if (window.$toast) {
                        window.$toast.success('Background image uploaded successfully')
                    }
                }
                reader.onerror = function() {
                    if (window.$toast) {
                        window.$toast.error('Failed to read file. Please try again.')
                    }
                    event.target.value = ''
                }
                reader.onabort = function() {
                    if (window.$toast) {
                        window.$toast.warning('File reading was cancelled')
                    }
                }
                reader.readAsDataURL(file)
            }
        },

        /**
         * Generate Link-in-Bio URL
         */
        generateLinkInBioUrl() {
            if (!this.form.name || this.form.name.trim() === '') {
                if (window.$toast) {
                    window.$toast.warning('Please enter your name to generate a link.')
                }
                return
            }

            if (this.form.links.length === 0) {
                if (window.$toast) {
                    window.$toast.warning('Please add at least one link.')
                }
                return
            }

            this.loading = true
            this.form.processing = true

            const payload = {
                name: this.form.name,
                title: this.form.title,
                bio: this.form.bio,
                profile_picture: this.form.profile_picture,
                links: this.form.links,
                button_style: this.form.button_style,
                button_color: this.form.button_color,
                background_color: this.form.background_color,
                text_color: this.form.text_color,
                background_image: this.form.background_image,
                enable_lead_capture: this.form.enable_lead_capture,
                lead_form_type: this.form.lead_form_type,
            }

            // Use route helper for consistency
            this.axios.post(this.route('linkinbio.save'), payload).then((response) => {
                if (response.data && response.data.success) {
                    this.generatedUrl = response.data.url
                    this.loading = false
                    this.form.processing = false
                } else {
                    const errorMessage = response.data?.message || response.data?.error || 'Failed to generate link'
                    throw new Error(errorMessage)
                }
            }).catch((error) => {
                console.error('Error generating link:', error)
                this.loading = false
                this.form.processing = false

                let errorMessage = 'Error generating link. Please try again.'
                if (error.response) {
                    if (error.response.status === 422) {
                        errorMessage = 'Validation failed. Please check your inputs.'
                    } else if (error.response.status === 401 || error.response.status === 403) {
                        errorMessage = 'You do not have permission to perform this action.'
                    } else if (error.response.status === 500) {
                        errorMessage = 'Server error. Please try again later.'
                    } else if (error.response.data?.message || error.response.data?.error) {
                        errorMessage = error.response.data.message || error.response.data.error
                    }
                } else if (error.message) {
                    if (error.message.includes('Network') || error.message.includes('timeout')) {
                        errorMessage = 'Network error. Please check your connection and try again.'
                    } else {
                        errorMessage = error.message
                    }
                }

                if (window.$toast) {
                    window.$toast.error(errorMessage)
                }
            })
        },

        /**
         * Copy URL to clipboard
         */
        async copyUrl() {
            if (!this.generatedUrl) {
                if (window.$toast) {
                    window.$toast.warning('Please generate a link first')
                }
                return
            }

            try {
                // Try modern clipboard API first
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(this.generatedUrl)
                } else if (this.$refs.urlInput) {
                    // Fallback for older browsers
                    this.$refs.urlInput.select()
                    document.execCommand('copy')
                } else {
                    throw new Error('Clipboard API not available')
                }

                // Show success feedback
                if (window.$toast) {
                    window.$toast.success('URL copied to clipboard!')
                }
            } catch (err) {
                console.error('Failed to copy URL:', err)
                if (window.$toast) {
                    window.$toast.error('Failed to copy URL. Please try again.')
                }
            }
        },

        /**
         * Download Link-in-Bio page as PNG
         */
        downloadAsPNG() {
            this.loading = true
            const target = this.$refs.linkBioPreview

            if (!target) {
                console.error('Preview element not found')
                this.loading = false
                return
            }

            toPng(target, {
                pixelRatio: 3,
                backgroundColor: this.form.background_color
            }).then((dataUrl) => {
                this.loading = false
                const link = document.createElement('a')
                link.download = `link-in-bio-${Date.now()}.png`
                link.href = dataUrl
                link.click()
            }).catch((err) => {
                console.error('Error generating image:', err)
                this.loading = false
                if (window.$toast) {
                    window.$toast.error('Error generating image. Please try again.')
                }
            })
        },

        /**
         * Set form values
         */
        setForm(elements){
            const that = this
            Object.keys(elements).forEach(function (element) {
                that.form[element] = elements[element]
            })
        },
    },
}
</script>





